import type { ConversationError } from "@app/types/assistant/conversation";
import { isAPIErrorResponse } from "@app/types/error";
import { safeParseJSON } from "@app/types/shared/utils/json_utils";
import {
  AlertCircle,
  Button,
  Icon,
  LinkWrapper,
  LogIn01,
} from "@dust-tt/sparkle";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";

interface ConversationErrorProps {
  error: ConversationError;
}

export function ConversationErrorDisplay({ error }: ConversationErrorProps) {
  const errorMessageRes = safeParseJSON(JSON.stringify(error));

  if (errorMessageRes.isErr() || !isAPIErrorResponse(errorMessageRes.value)) {
    return <ConversationGenericError />;
  }

  switch (errorMessageRes.value.error.type) {
    case "conversation_access_restricted":
      return <ConversationAccessRestricted />;

    case "conversation_not_found":
      return <ConversationNotFound />;

    default:
      return <ConversationGenericError />;
  }
}

function ConversationAccessRestricted() {
  const { t } = useTranslation();
  return (
    <ErrorDisplay
      icon={AlertCircle}
      title={t(
        "conv.accessRestrictedTitle",
        "You don't have access to this page"
      )}
      message={[
        t(
          "conv.accessRestrictedBody",
          "This conversation may include restricted data."
        ),
      ]}
    />
  );
}

function ConversationNotFound() {
  const { t } = useTranslation();
  return (
    <ErrorDisplay
      icon={AlertCircle}
      title={t("conv.conversationNotFoundTitle", "Conversation Not Found")}
      message={t(
        "conv.notFoundBody",
        "This conversation may have been deleted or moved."
      )}
    />
  );
}

function ConversationGenericError() {
  const { t } = useTranslation();
  return (
    <ErrorDisplay
      title={t("conv.errorLoadingConversation", "Error Loading Conversation")}
      message={[
        t(
          "conv.loadErrorBody",
          "Something went wrong while loading the conversation."
        ),
        t("conv.tryAgainLater", "Please try again later."),
      ]}
    />
  );
}

interface ErrorDisplayProps {
  icon?: ComponentType<{
    className?: string;
  }>;
  message: string | string[];
  title: string;
}

export function ErrorDisplay({ icon, message, title }: ErrorDisplayProps) {
  const { t } = useTranslation();
  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-3">
      {icon && <Icon visual={icon} className="text-info-400" size="lg" />}
      <p className="heading-xl text-center text-foreground">{title}</p>
      <div className="copy-sm text-center text-muted-foreground">
        {Array.isArray(message) ? (
          message.map((line, index) => <p key={index}>{line}</p>)
        ) : (
          <p>{message}</p>
        )}
      </div>
      <LinkWrapper href="/">
        <Button
          variant="outline"
          label={t("conv.backToHomepage", "Back to homepage")}
          icon={LogIn01}
        />
      </LinkWrapper>
    </div>
  );
}
